interface StatusBadgeProps {
    variant: "default" | "secondary" | "outline"
    className: string
}

export const STATUS_ORDER = [
    "pending",
    "procuring",
    "scheduled",
    "inTransit",
    "in-transit",
    "in transit",
    "out-for-delivery",
    "delivered",
    "fulfilled",
    "cancelled",
    "canceled",
    "open",
    "closed",
] as const

export function getStatusBadgeProps(status?: string): StatusBadgeProps {
    switch (status) {
        case "open":
            return {
                variant: "default",
                className:
                    "bg-blue-100 text-blue-700 hover:bg-blue-100 dark:bg-blue-950/50 dark:text-blue-200 dark:hover:bg-blue-950/50",
            }
        case "closed":
            return {
                variant: "default",
                className:
                    "bg-gray-100 text-gray-600 hover:bg-gray-100 dark:bg-gray-800/60 dark:text-gray-300 dark:hover:bg-gray-800/60",
            }
        case "pending":
            return {
                variant: "secondary",
                className:
                    "bg-gray-100 text-gray-600 hover:bg-gray-100 dark:bg-gray-800/60 dark:text-gray-300 dark:hover:bg-gray-800/60",
            }
        case "procuring":
            return {
                variant: "default",
                className:
                    "bg-amber-100 text-amber-700 hover:bg-amber-100 dark:bg-amber-950/50 dark:text-amber-200 dark:hover:bg-amber-950/50",
            }
        case "scheduled":
            return {
                variant: "default",
                className:
                    "bg-blue-100 text-blue-700 hover:bg-blue-100 dark:bg-blue-950/50 dark:text-blue-200 dark:hover:bg-blue-950/50",
            }
        case "inTransit":
        case "in-transit":
        case "in transit":
            return {
                variant: "default",
                className:
                    "bg-orange-100 text-orange-700 hover:bg-orange-100 dark:bg-orange-950/50 dark:text-orange-200 dark:hover:bg-orange-950/50",
            }
        case "delivered":
            return {
                variant: "default",
                className:
                    "bg-green-100 text-green-700 hover:bg-green-100 dark:bg-green-950/50 dark:text-green-200 dark:hover:bg-green-950/50",
            }
        case "fulfilled":
            return {
                variant: "default",
                className:
                    "bg-green-100 text-green-700 hover:bg-green-100 dark:bg-green-950/50 dark:text-green-200 dark:hover:bg-green-950/50",
            }
        case "cancelled":
        case "canceled":
            return {
                variant: "default",
                className:
                    "bg-slate-100 text-slate-700 hover:bg-slate-100 dark:bg-slate-950/50 dark:text-slate-200 dark:hover:bg-slate-950/50",
            }
        case "out-for-delivery":
            return {
                variant: "default",
                className:
                    "bg-indigo-100 text-indigo-700 hover:bg-indigo-100 dark:bg-indigo-950/50 dark:text-indigo-200 dark:hover:bg-indigo-950/50",
            }
        case "draft":
            return {
                variant: "outline",
                className:
                    "bg-gray-100 text-gray-500 hover:bg-gray-100 dark:bg-gray-800/60 dark:text-gray-300 dark:hover:bg-gray-800/60",
            }
        case "sent":
            return {
                variant: "default",
                className:
                    "bg-blue-100 text-blue-700 hover:bg-blue-100 dark:bg-blue-950/50 dark:text-blue-200 dark:hover:bg-blue-950/50",
            }
        case "active":
            return {
                variant: "default",
                className:
                    "bg-blue-100 text-blue-700 hover:bg-blue-100 dark:bg-blue-950/50 dark:text-blue-200 dark:hover:bg-blue-950/50",
            }
        case "inactive":
        case "discontinued":
            return {
                variant: "default",
                className:
                    "bg-gray-100 text-gray-600 hover:bg-gray-100 dark:bg-gray-800/60 dark:text-gray-300 dark:hover:bg-gray-800/60",
            }
        case "received":
            return {
                variant: "default",
                className:
                    "bg-blue-100 text-blue-700 hover:bg-blue-100 dark:bg-blue-950/50 dark:text-blue-200 dark:hover:bg-blue-950/50",
            }
        case "declined":
        case "failed":
        case "undelivered":
            return {
                variant: "default",
                className:
                    "bg-red-100 text-red-700 hover:bg-red-100 dark:bg-red-950/50 dark:text-red-200 dark:hover:bg-red-950/50",
            }
        case "accepted":
            return {
                variant: "default",
                className:
                    "bg-emerald-100 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-950/50 dark:text-emerald-200 dark:hover:bg-emerald-950/50",
            }
        default:
            return {
                variant: "outline",
                className: "",
            }
    }
}

const STATUS_KEY_MAP: Record<string, string> = {
    pending: "pending",
    procuring: "procuring",
    scheduled: "scheduled",
    intransit: "inTransit",
    delivered: "delivered",
    cancelled: "cancelled",
    canceled: "cancelled",
    onhold: "onHold",
    quotelost: "quoteLost",
    invoicereceived: "invoiceReceived",
    invoiceflagged: "invoiceFlagged",
    invoicepaid: "invoicePaid",
    open: "open",
    closed: "closed",
    active: "active",
    complete: "closed",
    draft: "draft",
    sent: "sent",
    accepted: "accepted",
    rejected: "rejected",
    expired: "expired",
    inprogress: "inProgress",
    fulfilled: "fulfilled",
    partialfulfilled: "partialFulfilled",
    unfulfilled: "unfulfilled",
    partial: "partial",
}

export const SERVICE_TYPE_LABEL_KEYS = new Set([
    "ltl",
    "tl",
    "ftl",
    "parcel",
    "ocean",
    "air",
    "intermodal",
    "drayage",
    "lastMile",
    "willCall",
])
export const EQUIPMENT_LABEL_KEYS = new Set([
    "dryVan",
    "reefer",
    "flatbed",
    "stepDeck",
    "lowboy",
    "rgn",
    "tanker",
    "intermodal",
    "sprinterVan",
    "boxTruck",
    "hotshot",
    "other",
])

export const VENDOR_TYPE_LABEL_KEYS = new Set([
    "carrier",
    "broker",
    "customsBroker",
    "freightForwarder",
    "warehouse",
    "supplier",
    "other",
])

export const CONTACT_ROLE_LABEL_KEYS = new Set([
    "billing",
    "dispatch",
    "quoting",
    "pickup",
    "other",
])

export const TRANSLATED_STATUS_KEYS = new Set([
    "accepted",
    "active",
    "cancelled",
    "closed",
    "declined",
    "delivered",
    "discontinued",
    "draft",
    "expired",
    "fulfilled",
    "inactive",
    "inProgress",
    "inTransit",
    "invoiceFlagged",
    "invoicePaid",
    "invoiceReceived",
    "onHold",
    "open",
    "partial",
    "partialFulfilled",
    "pending",
    "procuring",
    "quoteLost",
    "quoting",
    "received",
    "rejected",
    "scheduled",
    "sent",
    "unfulfilled",
])

export function getStatusDotClass(status?: string): string {
    switch (statusToTranslationKey(status ?? "")) {
        case "procuring":
            return "bg-amber-500"
        case "scheduled":
        case "open":
        case "sent":
        case "active":
        case "invoiceReceived":
            return "bg-blue-500"
        case "inTransit":
            return "bg-orange-500"
        case "delivered":
        case "fulfilled":
        case "accepted":
        case "invoicePaid":
            return "bg-emerald-500"
        case "invoiceFlagged":
        case "rejected":
        case "declined":
        case "failed":
        case "undelivered":
            return "bg-red-500"
        default:
            return "bg-muted-foreground/40"
    }
}

type StatusTone =
    "neutral" | "warning" | "active" | "transit" | "success" | "error"

export function getStatusTone(status?: string): StatusTone {
    if (normalizeStatus(status ?? "") === "complete") return "success"

    switch (statusToTranslationKey(status ?? "")) {
        case "procuring":
        case "onHold":
            return "warning"
        case "scheduled":
        case "open":
        case "sent":
        case "active":
        case "invoiceReceived":
            return "active"
        case "inTransit":
            return "transit"
        case "delivered":
        case "fulfilled":
        case "accepted":
        case "invoicePaid":
            return "success"
        case "invoiceFlagged":
        case "rejected":
        case "declined":
        case "failed":
        case "undelivered":
        case "quoteLost":
            return "error"
        default:
            return "neutral"
    }
}

function normalizeStatus(status: string): string {
    return status.toLowerCase().replace(/[\s-]/g, "")
}

export function statusToTranslationKey(status: string): string {
    return STATUS_KEY_MAP[normalizeStatus(status)] || status
}

export function formatStatus(status?: string): string {
    if (!status) return ""
    return status
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .split("-")
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
}

export function getStatusOrder(status?: string): number {
    if (!status) return STATUS_ORDER.length
    const index = STATUS_ORDER.indexOf(status as (typeof STATUS_ORDER)[number])
    return index === -1 ? STATUS_ORDER.length : index
}

export function extractLocationFromMarkdown(
    markdown: string,
    type: "origin" | "destination"
): string | null {
    if (!markdown || typeof markdown !== "string") return null

    const pattern =
        type === "origin"
            ? /\*?\*?(Origin|Origen):?\*?\*?\s*([^\n]+)/i
            : /\*?\*?(Destination|Destino):?\*?\*?\s*([^\n]+)/i

    const match = markdown.match(pattern)
    if (match) {
        const [_fullMatch, _keyword, location] = match
        if (location) {
            return location.trim()
        }
    }

    const routePattern =
        /→\s*(Route|Ruta)[\s\S]*?\*?\*?(Origin|Origen|Destination|Destino):?\*?\*?\s*([^\n]+)/gi
    const routeMatches = [...markdown.matchAll(routePattern)]
    for (const routeMatch of routeMatches) {
        const [_fullMatch, _routeKeyword, locationKeyword, location] =
            routeMatch
        const matchedType = locationKeyword.toLowerCase()
        if (
            matchedType === type ||
            (type === "origin" && matchedType === "origen") ||
            (type === "destination" && matchedType === "destino")
        ) {
            return location.trim()
        }
    }

    const laneMatch = markdown.match(/\*\*Lane:\*\*\s*([^\n]+)/)
    if (laneMatch) {
        const laneValue = laneMatch[1].split(/\||(?=\*\*)/)[0].trim()
        const parts = laneValue.split(/→|->/).map(s => s.trim())
        if (type === "origin" && parts[0]) return parts[0]
        if (type === "destination" && parts[1]) return parts[1]
    }

    return null
}

export function getShortLocation(
    location: string | { city?: string; name?: string; state?: string }
): string {
    if (!location) return ""

    if (typeof location === "object" && "city" in location) {
        return location.city || ""
    }

    if (typeof location !== "string") return ""

    const parts = location.split(",")
    if (parts.length >= 2) {
        const cityPart = parts[parts.length - 2].trim()
        const cityWords = cityPart.split(" ")
        if (cityWords.length > 3) {
            return cityWords.slice(-2).join(" ")
        }
        return cityPart
    }

    const words = location.split(" ")
    if (words.length > 1 && words[words.length - 1].length === 2) {
        words.pop()
    }
    if (words.length > 2) {
        return words.slice(-2).join(" ")
    }

    return location
}
